import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, Sparkles, Calendar, Newspaper, Shield, Radar, FileText, Wand2, BarChart3, Globe, Ship, Zap, Users, Database } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const APP_VERSION = "3.0";
const DEPLOY_DATE = "08/03/2026";

// Historique des versions et fonctionnalités
const VERSION_HISTORY = [
  {
    version: "3.0",
    date: "31/03/2026",
    title: "Integration IRONSIGHT - Carte & Marches",
    features: [
      { name: "Carte Interactive Leaflet", icon: Globe, description: "Carte theatre avec couches ADS-B, NASA FIRMS, Telegram Live" },
      { name: "ADS-B Military", icon: Radar, description: "Suivi aeronefs militaires temps reel (adsb.lol)" },
      { name: "NASA FIRMS", icon: Zap, description: "Detection thermique satellite VIIRS + alertes explosions" },
      { name: "Telegram Live", icon: Newspaper, description: "26 canaux scraped, traduction auto AR/HE/FA" },
      { name: "Marches Financiers", icon: BarChart3, description: "Defense stocks, Crypto, Polymarket predictions" },
      { name: "Analyse Propagande IA", icon: Shield, description: "Score de propagande Claude Sonnet (0-10)" },
    ],
    isNew: true
  },
  {
    version: "2.6.0",
    date: "08/03/2026",
    title: "Source Health Monitor",
    features: [
      { name: "Health Monitoring", icon: Shield, description: "Surveillance sante des 83 sources RSS en temps reel" },
      { name: "Reessai Automatique", icon: Zap, description: "Sources HS reessayees toutes les 24h automatiquement" },
      { name: "Dashboard Sante", icon: BarChart3, description: "Vue detaillee: healthy, degraded, failing par source" },
      { name: "Brotli Support", icon: Database, description: "Compression Brotli pour plus de sources compatibles" },
    ],
    isNew: false
  },
  {
    version: "2.4.1",
    date: "07/03/2026",
    title: "Alertes Stratégiques & Magic Report+",
    features: [
      { name: "Alertes Stratégiques", icon: Shield, description: "Détection auto des mots-clés (France, OTAN, Rafale, Hormuz...)" },
      { name: "Notification au login", icon: Database, description: "Modal 'Vous avez X alertes non lues'" },
      { name: "Onglet ALERTES", icon: Zap, description: "Nouvel onglet avec compteur de notifications" },
      { name: "Magic Report étendu", icon: Newspaper, description: "Section Veille Adversaires intégrée au rapport" },
    ],
    isNew: false
  },
  {
    version: "2.4.0",
    date: "06/03/2026",
    title: "Veille Adversaires & Sources Premium",
    features: [
      { name: "Mode Adversaires", icon: Shield, description: "Scan isolé des sources russes, chinoises et iraniennes" },
      { name: "81 sources média", icon: Newspaper, description: "12 sources adversaires + 69 sources libres" },
      { name: "Sources Premium", icon: Database, description: "Interface pour credentials presse (FT, Janes, Lloyd's...)" },
      { name: "Cache GDELT optimisé", icon: Zap, description: "Réduction des erreurs de rate limiting" },
    ],
    isNew: false
  },
  {
    version: "2.2.0",
    date: "05/03/2026",
    title: "Magic Report & Analyse LLM",
    features: [
      { name: "Magic Report", icon: Wand2, description: "Rapport PDF 30+ pages auto-généré" },
      { name: "Analyse LLM", icon: Sparkles, description: "Claude Sonnet analyse les actualités" },
      { name: "Auto-correction", icon: CheckCircle2, description: "Validation et cohérence des données" },
      { name: "Historique rapports", icon: BarChart3, description: "Magic Reports accessibles dans l'historique" },
    ]
  },
  {
    version: "2.1.0",
    date: "04/03/2026", 
    title: "Intégration AIS & Early Warning",
    features: [
      { name: "AISStream.io", icon: Ship, description: "Données trafic maritime temps réel" },
      { name: "Système EWS", icon: Shield, description: "Alerte précoce multi-indicateurs" },
      { name: "Simulations Monte Carlo", icon: BarChart3, description: "9 scénarios avec 500+ itérations" },
    ]
  },
  {
    version: "2.0.0",
    date: "03/03/2026",
    title: "Refonte complète",
    features: [
      { name: "Multi-théâtres", icon: Globe, description: "Ormuz, Mer Rouge, Malacca, Suez, Mer Noire" },
      { name: "Export PDF/Excel", icon: FileText, description: "Rapports exportables" },
      { name: "Interface tactique", icon: Radar, description: "Design inspiré systèmes militaires" },
    ]
  }
];

// Sources par catégorie
const SOURCES_BY_CATEGORY = {
  "Énergie": ["Reuters Business", "EIA Today", "IEA News", "OilPrice", "Rigzone", "S&P Platts", "World Oil", "Energy Voice", "Petroleum Economist"],
  "Maritime": ["Naval Today", "gCaptain", "Offshore Energy", "Lloyd's List", "Splash 247", "Seatrade Maritime", "Hellenic Shipping News", "Ship Technology", "Safety4Sea", "Port Technology", "Le Marin"],
  "Défense": ["Defense News", "Janes", "Defense One", "War on the Rocks", "Breaking Defense", "The War Zone", "Naval News", "Army Recognition", "Zone Militaire", "Meta-Defense", "Air & Cosmos", "Armées.com"],
  "Géopolitique": ["BBC World", "BBC Middle East", "The Guardian", "NYT", "WSJ", "Al Jazeera", "Arab News", "Gulf News", "Middle East Eye", "Times of Israel", "Foreign Affairs", "CSIS", "Atlantic Council", "RAND", "IFRI", "The Diplomat", "Politico EU"],
  "Francophones": ["Le Monde", "Le Figaro", "France24", "RFI", "Les Échos", "La Tribune", "L'Opinion"],
  "Adversaires": ["RT (Russia Today)", "TASS", "Sputnik News", "RIA Novosti", "Xinhua", "Global Times", "CGTN", "China Daily", "Press TV (Iran)", "Fars News", "IRNA", "Mehr News"]
};

const FeaturesPanel = ({ isOpen, onClose, showOnlyNew = false }) => {
  const [activeTab, setActiveTab] = useState('features');
  
  if (!isOpen) return null;

  const totalSources = Object.values(SOURCES_BY_CATEGORY).flat().length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-marine-navy border-2 border-marine-cyan/50 rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-marine-cyan/30 bg-gradient-to-r from-marine-navy to-black">
            <div>
              <h2 className="font-heading text-marine-cyan text-xl tracking-wider">
                CESM OSINT SYSTEM
              </h2>
              <p className="font-mono text-marine-amber text-sm">
                Version {APP_VERSION} • Déployé le {DEPLOY_DATE}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center"
              data-testid="close-features-btn"
            >
              <X className="w-7 h-7" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-marine-slate/30">
            <button
              onClick={() => setActiveTab('features')}
              className={`flex-1 px-4 py-3 font-mono text-sm uppercase ${
                activeTab === 'features' 
                  ? 'bg-marine-cyan/20 text-marine-cyan border-b-2 border-marine-cyan' 
                  : 'text-white/90'
              }`}
            >
              Fonctionnalités
            </button>
            <button
              onClick={() => setActiveTab('sources')}
              className={`flex-1 px-4 py-3 font-mono text-sm uppercase ${
                activeTab === 'sources' 
                  ? 'bg-marine-cyan/20 text-marine-cyan border-b-2 border-marine-cyan' 
                  : 'text-white/90'
              }`}
            >
              Sources ({totalSources})
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[55vh] p-4">
            {activeTab === 'features' ? (
              <div className="space-y-6">
                {VERSION_HISTORY
                  .filter(v => !showOnlyNew || v.isNew)
                  .map((version, idx) => (
                  <div key={version.version} className={`${idx === 0 ? 'bg-marine-cyan/10 border-marine-cyan' : 'bg-black/30 border-marine-slate/30'} border rounded-lg p-4`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="font-heading text-marine-cyan text-lg">v{version.version}</span>
                        {version.isNew && (
                          <span className="px-2 py-0.5 bg-marine-green text-black text-xs font-bold rounded">NOUVEAU</span>
                        )}
                      </div>
                      <span className="font-mono text-white/80 text-sm flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {version.date}
                      </span>
                    </div>
                    <p className="font-mono text-white/80 text-sm mb-3">{version.title}</p>
                    <div className="grid gap-2">
                      {version.features.map((feature, fidx) => (
                        <div key={fidx} className="flex items-start gap-3 p-2 bg-black/30 rounded">
                          <feature.icon className="w-5 h-5 text-marine-cyan flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-mono text-white text-sm font-bold">{feature.name}</p>
                            <p className="font-mono text-white/80 text-xs">{feature.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(SOURCES_BY_CATEGORY).map(([category, sources]) => (
                  <div key={category} className="bg-black/30 border border-marine-slate/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Database className="w-4 h-4 text-marine-cyan" />
                      <span className="font-mono text-marine-cyan text-sm font-bold uppercase">{category}</span>
                      <span className="font-mono text-white/80 text-xs">({sources.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {sources.map((source, idx) => (
                        <span key={idx} className="px-2 py-1 bg-marine-slate/30 text-white/80 text-xs font-mono rounded">
                          {source}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-marine-slate/30 bg-black/30">
            <p className="font-mono text-white/80 text-xs text-center">
              CESM OSINT System • Concept & Développement: CDT (R) Jean-Baptiste GIRAUD
            </p>
            <p className="font-mono text-white/40 text-[10px] text-center mt-1">
              LPP Logiciel &copy; 2026 &bull; Demandeur: Vice-Amiral Laurent BECHLER (CESM) &bull; Conseil Strategique: COL (R) Thierry ASSONION
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Hook pour gérer l'affichage au premier login / nouvelle version
export const useFeatureAnnouncement = (user) => {
  const [showFeatures, setShowFeatures] = useState(false);
  const [showOnlyNew, setShowOnlyNew] = useState(false);

  useEffect(() => {
    if (!user) return;

    const lastSeenVersion = localStorage.getItem('cesm_last_seen_version');
    const hasSeenOnboarding = localStorage.getItem('cesm_onboarding_complete');

    if (!hasSeenOnboarding) {
      // Premier login - montrer toutes les fonctionnalités
      setShowFeatures(true);
      setShowOnlyNew(false);
      localStorage.setItem('cesm_onboarding_complete', 'true');
      localStorage.setItem('cesm_last_seen_version', APP_VERSION);
    } else if (lastSeenVersion !== APP_VERSION) {
      // Nouvelle version - montrer seulement les nouveautés
      setShowFeatures(true);
      setShowOnlyNew(true);
      localStorage.setItem('cesm_last_seen_version', APP_VERSION);
    }
  }, [user]);

  return { showFeatures, setShowFeatures, showOnlyNew };
};

export default FeaturesPanel;
